import { formatCurrency } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, Input, LOCALE_ID, OnDestroy, OnInit, signal, ViewChild, inject } from "@angular/core";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartBindingObject, ChartDialogService, ChartManagerService, ChartRegistryService, ChartService } from "@tylertech/charting-module";
import { Command, IconCommand } from "@tylertech/commands-module";
import { formatAbbreviatedNumber } from "@tylertech/common-components-module";
import { FileService, LoggingService } from "@tylertech/core-module";
import { ICustomViewComponent } from "@tylertech/custom-view";
import { IChartCriteria } from "@tylertech/tyler-api-types";
import { ChartConfiguration, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    selector: "student-activities-monthly-expenditure-chart",
    templateUrl: "./expenditureChart.tmpl.html",
    standalone: false
})
export class ExpenditureChartComponent implements OnDestroy, OnInit, ICustomViewComponent<ChartBindingObject>
{
    private readonly locale = inject(LOCALE_ID);
    private readonly $chartRegistryService = inject(ChartRegistryService);
    private readonly $loggingService = inject(LoggingService);
    private readonly $chartDialogService = inject(ChartDialogService);
    private readonly $fileService = inject(FileService);
    private readonly $chartService = inject(ChartService);

    private readonly destructor$ = new Subject<void>();
    readonly commands: ReadonlyArray<Command>;
    private readonly _chartId: string;

    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;
    readonly showChart = signal(false);
    chartConfig: ChartConfiguration<"bar">;

    private _chartCriteria: IChartCriteria;
    readonly customDataBinding: ChartBindingObject;
    readonly ChartTitle: string = "Student Activity Monthly Expenditure";
    isLoaded: boolean;
    constructor()
    {
        const $chartManagerService = inject(ChartManagerService);

        $chartManagerService.refreshChartObservable.pipe(takeUntil(this.destructor$)).subscribe((chartId: string) =>
        {
            if (chartId !== this.chartId)
            {
                return;
            }
            this.loadChartData();
        });
        this.commands = [
            new IconCommand({
                icon: "share",
                label: null,
                action: () => this.shareChart(),
                getEnabled: () => this.chartHasData,
                tooltip: `Share ${this.ChartTitle}`,
            }),
        ];
    }

    get chartId(): string | undefined
    {
        return this._chartId || this.customDataBinding?.chartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria || this.customDataBinding?.chartCriteria;
    }

    set chartCriteria(value: IChartCriteria)
    {
        this._chartCriteria = value;
    }

    get chartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    get ChartSubtitle(): string
    {
        let ret = "";
        const fiscal = this.chartCriteria?.Parameters?.find(x => x.Id === "SAExpenditureFiscal");
        const month = this.chartCriteria?.Parameters?.find(x => x.Id === "SAExpenditureMonth");
        if (fiscal?.Value)
        {
            const selectedFiscal = fiscal.Options.find(x => x.Value === fiscal.Value.toString());
            if (selectedFiscal)
            {
                ret += selectedFiscal.Display;
            }
        }
        if (month?.Value)
        {
            const monthName = month.Options.find(x => x.Value === month.Value.toString()).Display;
            ret += " &bull; " + monthName;
        }
        return ret;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        new Promise((_resolveFn, rejectFn) =>
        {
            if (!this.hasData())
            {
                return this.loadChartData();
            }
            try
            {
                this.updateChartConfiguration();
            }
            catch (err)
            {
                rejectFn(err);
            }
        })
            .catch(e =>
            {
                console.error("Error on load", e);
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    hasData(): boolean
    {
        return this.chartCriteria?.ChartDatas?.length > 0;
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService
            .GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                }
            )
            .then(
                () =>
                {
                    if (!this.hasData())
                    {
                        return;
                    }
                    try
                    {
                        this.updateChartConfiguration();
                    }
                    catch (err)
                    {
                        return Promise.reject(err);
                    }
                },
                (err: unknown) =>
                {
                    console.error(err);
                }
            )
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    openSettings(): void
    {
        this.$chartDialogService
            .showChartParametersDialog(this.ChartTitle, this.chartCriteria)
            .then((criteria: IChartCriteria) =>
            {
                this.chartCriteria = criteria;
                this.loadChartData();
            });
    }

    private shareChart(): void
    {
        const img = this.chartContainer.createImage();
        this.$fileService.openDocumentLink(img, "image/png", this.ChartTitle.replace(/ /g, ""));
    }

    private updateChartConfiguration(): void
    {
        const chartColors = this.$chartService.getColors();

        this.chartConfig = {
            type: "bar",
            options: {
                responsive: true,
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        callbacks: {
                            title: (tooltipItems: TooltipItem<"bar">[]) =>
                            {
                                return tooltipItems[0].label;
                            },
                            label: (tooltipItem: TooltipItem<"bar">) =>
                            {
                                return formatCurrency(Number(tooltipItem.raw), this.locale, "$");
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        type: "category",
                        display: true,
                        grid: { display: true },
                        labels: this.chartCriteria.ChartDatas[0].Labels,
                    },
                    y: {
                        type: "linear",
                        display: true,
                        position: "left",
                        grid: { display: true },
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(value > -10 && value < 10 ? value.toFixed(2) : value.toFixed(0));
                            },
                        },
                    },
                },
            },
            data: {
                labels: this.chartCriteria.ChartDatas[0].Labels,
                datasets: [
                    {
                        type: "bar",
                        label: `${this.chartCriteria.ChartDatas[0].SetLabel}`,
                        data: this.chartCriteria.ChartDatas[0].Data,
                        backgroundColor: chartColors,
                        categoryPercentage: 0.5,
                        barPercentage: 0.8,
                    },
                ],
            },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }
}
